import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import _isEmpty from 'lodash/isEmpty';
import _pick from 'lodash/pick';
import dayjs from 'dayjs';
import { TaskTimelineFrameEnum } from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import Modal from '@lib/components/Modal/Modal';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import formStyles from '@lib/assets/styles/Form.module.scss';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import _without from 'lodash/without';
import DateRangePicker from '@lib/components/DateTimePicker/DateRangePicker';
import { InputSizes, InputTypes } from '@lib/components/Input/enums';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import { revertTransformDate, transformDate } from '@lib/utils/dateTimeHelpers';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { getPeriodEndDate } from './utils/getPeriodDate';
import useQueries from './utils/useQueries';
import styles from './TasksTimelineFilter.module.scss';
import useFilterFields, {
  TASKS_TIMELINE_FILTER_EMPTY_VALUES,
} from './utils/useFilterFields';
import TasksTimelineFilterActions from './TasksTimelineFilterActions';

const filterModal = Modals.DashboardTimelineFilterModal;
const modals = [filterModal];

type Props = {
  resetPeriodFilterParams: () => void;
  resetFilterParams: () => void;
  onUpdateFilterParams: (values: Values) => void;
  filterParams: Values;
  minDate?: string;
  maxDate?: string;
  basePeriodStartParam: string;
  basePeriodEndParam: string;
};

export default function TasksTimelineFilter(props: Props) {
  const {
    resetFilterParams,
    filterParams,
    resetPeriodFilterParams,
    onUpdateFilterParams,
    minDate,
    maxDate,
    basePeriodStartParam,
    basePeriodEndParam,
  } = props;
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);
  const dateRangePickerRef = useRef(undefined);
  const { getFormCategoriesLoadOptions, getSitesLoadOptions } = useQueries();

  const useModalManagementResult = useModalManagement(modals);
  const { isModalOpened, openModal, closeModal } = useModalManagementResult;
  const openFilter = () => openModal(filterModal);
  const closeFilter = () => closeModal(filterModal);
  const clearFilter = () => {
    closeModal(filterModal);
    resetFilterParams();
  };

  const periodValue = (filterParams[
    TasksFilterFieldNames.Period
  ] as string[]) || [basePeriodStartParam, basePeriodEndParam];

  const min = minDate ? dayjs(minDate).startOf('day') : undefined;
  const max = maxDate ? dayjs(maxDate).endOf('day') : undefined;

  const defaultValues =
    filterParams && Object.keys(filterParams).length > 0 ? filterParams : {};
  const view =
    filterParams[TasksFilterFieldNames.TimelineView] ||
    TASKS_TIMELINE_FILTER_EMPTY_VALUES[TasksFilterFieldNames.TimelineView];

  const filterCounter = Object.values(
    _pick(
      defaultValues,
      TasksFilterFieldNames.Site,
      TasksFilterFieldNames.FormCategory,
      TasksFilterFieldNames.ValidityHints,
    ),
  ).filter((value) => {
    if (Array.isArray(value)) {
      return !!_without(value, '').length;
    }
    return !_isEmpty(value);
  }).length;

  const fields = useFilterFields({
    getFormCategoriesLoadOptions,
    getSitesLoadOptions,
    t,
  });

  const onSubmit = (values: Values) => {
    onUpdateFilterParams(values);
    closeFilter();
  };

  const onTimeFilterChange = (v: string[]) => {
    if (Array.isArray(v) && v[0] && v[1]) {
      const periodEndDateObj = dayjs(revertTransformDate(v[1]));
      const periodEndValue = periodEndDateObj.isAfter(dayjs())
        ? getPeriodEndDate(
            transformDate(new Date()),
            view as TaskTimelineFrameEnum,
          )
        : periodEndDateObj.toDate();
      const periodEndValueStr = transformDate(periodEndValue, true);
      formRef.current?.setValue(TasksFilterFieldNames.Period, v);
      onUpdateFilterParams({
        ...filterParams,
        [TasksFilterFieldNames.Period]: v,
        [TasksFilterFieldNames.TimelineEndDate]: periodEndValueStr,
      });
    } else {
      resetPeriodFilterParams();
    }
  };

  return (
    <>
      <PageBody className={styles.filterWrap}>
        <WhiteBox
          className={styles.filter}
          verticalPaddings="extraSmall"
          horizontalPaddings="small"
        >
          <div className={styles.filterRow}>
            <div>
              <Chip
                counter={filterCounter}
                label={t('filter')}
                leftIcon="filter_alt"
                leftIconSymbolsOutlined
                onClick={openFilter}
                size={ChipSizes.large}
                type={ChipTypes.chipFilter}
                tooltipForCroppedTextDisabled
              />
            </div>
            {!!filterCounter && (
              <div>
                <Button
                  buttonSize={ButtonSizes.large}
                  buttonText={t('reset')}
                  buttonType={ButtonTypes.primaryText}
                  onClick={clearFilter}
                />
              </div>
            )}
          </div>
          <div className={styles.dateRangePickerCol}>
            <DateRangePicker
              label={t('period')}
              onChange={onTimeFilterChange}
              ref={dateRangePickerRef}
              size={InputSizes.medium}
              type={InputTypes.dateRange}
              value={periodValue}
              minDate={min}
              maxDate={max}
            />
          </div>
        </WhiteBox>
      </PageBody>
      <Modal
        isOpen={isModalOpened(filterModal)}
        caption={t('filter')}
        onClose={closeFilter}
      >
        <Form
          formRef={formRef}
          fields={fields}
          onSubmit={onSubmit}
          defaultValues={defaultValues}
          actionsComponent={TasksTimelineFilterActions}
          actionsComponentProps={{
            onCancel: closeFilter,
          }}
          formClassName={formStyles.form}
        />
      </Modal>
    </>
  );
}
